/**
 * Tracker registry — the one list of ad trackers TrafficPulse reads.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Every tracker has two spellings: the lowercase id used in routes, payloads
 * and bank `platform` columns ('tres-commas'), and the uppercase DB enum on
 * `smswiser_country_campaign.trafficProvider` ('TRES_COMMAS'). Convert with
 * the helpers in tracker-convert.ts — never by string transforms.
 */

export const TRACKER_IDS = ['voluum', 'binom', 'tres-commas'] as const;
export type TrackerId = (typeof TRACKER_IDS)[number];

export const TRACKER_ENUMS = ['VOLUUM', 'BINOM', 'TRES_COMMAS'] as const;
export type TrackerEnum = (typeof TRACKER_ENUMS)[number];

/** Trackers every deployment has; others appear only once configured. */
export const DEFAULT_ENABLED_TRACKERS: readonly TrackerId[] = ['voluum', 'binom'];

export interface TrackerCapabilities {
  /** Flow edits from TrafficPulse (weights, path on/off) + flow snapshots. */
  flowWrites: boolean;
  /** Flow Optimizer, its ranking tags and the campaign Flow / Scheduler tabs. */
  flowOptimizer: boolean;
  /** Bank-backed queue / rotation of the Flow Optimizer (promote, demote). */
  flowQueue: boolean;
  /** Automation gates may judge drops on this tracker's traffic. */
  automationGates: boolean;
  /** The bank pull (seed): networks, offers, landers and paths copied into
   *  bank rows stamped with this tracker, from the tracker-seed page. */
  bankPull: boolean;
  /** Dedicated scheduled bank pulls, separate from the legacy shared schedule. */
  bankAutoPull?: boolean;
  /** Dedicated guarded TC bank writes; legacy trackers keep their existing paths. */
  bankWrites?: boolean;
  /** Per-tracker offer cap recount and cap-driven queue changes. */
  offerCaps?: boolean;
  /** Isolated bank reports and tracker-qualified Overview reads. */
  bankStats?: boolean;
  /** Bank-page promote/demote/pause/requeue controls. */
  bankQueue?: boolean;
}

export interface TrackerInfo {
  id: TrackerId;
  enumValue: TrackerEnum;
  label: string;
  capabilities: TrackerCapabilities;
}

export const TRACKERS: Readonly<Record<TrackerId, TrackerInfo>> = {
  voluum: {
    id: 'voluum',
    enumValue: 'VOLUUM',
    label: 'Voluum',
    capabilities: {
      flowWrites: true,
      flowOptimizer: true,
      flowQueue: true,
      automationGates: true,
      bankPull: true,
    },
  },
  binom: {
    id: 'binom',
    enumValue: 'BINOM',
    label: 'Binom',
    capabilities: {
      flowWrites: true,
      flowOptimizer: true,
      flowQueue: true,
      automationGates: true,
      bankPull: true,
    },
  },
  'tres-commas': {
    id: 'tres-commas',
    enumValue: 'TRES_COMMAS',
    label: 'Tres Commas',
    // Queue and scheduled bank pulls validated on the isolated TC QA flow.
    capabilities: {
      flowWrites: true,
      flowOptimizer: true,
      flowQueue: true,
      automationGates: true,
      bankPull: true,
      bankAutoPull: true,
      bankWrites: true,
      offerCaps: true,
      bankStats: true,
      bankQueue: true,
    },
  },
};

export function trackerCan(id: TrackerId, capability: keyof TrackerCapabilities): boolean {
  return TRACKERS[id].capabilities[capability] === true;
}
